import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { SalesArticleDto } from '@api/models/sales-article-dto';
import { SalesArticleSummary } from '@api/models/sales-article-summary';
import {
  AddViewedSalesArticle,
  LoadSalesArticle,
  RefreshViewedSalesArticles,
  SetViewedSalesArticles
} from './sales-article.actions';
import { SalesArticlesService } from '@api/services/sales-articles.service';
import { viewedSalesArticleKey, WebStorageService } from '@app/modules/shared/services/web-storage/web-storage-service';

export class SalesArticleStateModel {
  salesArticle: SalesArticleDto;
  relatedSalesArticles: SalesArticleSummary[];
  variantSalesArticles: SalesArticleSummary[];
  viewed: SalesArticleDto[];
}

@State<SalesArticleStateModel>({
  name: 'sales_article',
  defaults: {
    salesArticle: null,
    relatedSalesArticles: [],
    variantSalesArticles: [],
    viewed: []
  }
})
@Injectable()
export class SalesArticleState {
  constructor(
    private salesArticleService: SalesArticlesService,
    private webStorageService: WebStorageService
  ) {}

  @Selector()
  static selectSalesArticle(state: SalesArticleStateModel) {
    return state.salesArticle;
  }

  @Selector()
  static selectViewed(state: SalesArticleStateModel) {
    return state.viewed?.slice(1) ?? [];
  }

  @Action(LoadSalesArticle)
  private load(ctx: StateContext<SalesArticleStateModel>, action: LoadSalesArticle) {
    ctx.patchState({ salesArticle: null });
    return this.salesArticleService
      .apiProductsquerySalesarticlesUrlIdDetailsGet({ urlId: action.id })
      .subscribe((response: SalesArticleDto) => {
        return ctx.patchState({ salesArticle: response });
      });
  }

  @Action(AddViewedSalesArticle)
  private addViewedSalesArticle(ctx: StateContext<SalesArticleStateModel>, action: AddViewedSalesArticle) {
    const state = ctx.getState();
    let viewed: SalesArticleDto[];

    return this.salesArticleService
      .apiProductsquerySalesarticlesSummaryIdGet({ id: action.salesArticleId })
      .subscribe((response: SalesArticleSummary) => {
        if (state.viewed.filter((art: SalesArticleDto) => !!art && art.id === action.salesArticleId).length > 0) {
          const viewedSalesArticle = state.viewed.filter((art) => art.id === action.salesArticleId)[0];
          viewed = [...state.viewed];
          viewed[viewed.indexOf(viewedSalesArticle)] = response;
        } else {
          if (state.viewed.length > 5) {
            viewed = [...state.viewed.slice(0, 5)];
          } else {
            viewed = [...state.viewed];
          }

          viewed = viewed.filter((val) => !!val);
          viewed.unshift(response);
        }

        this.webStorageService.set(viewedSalesArticleKey, viewed);

        return ctx.patchState({ viewed });
      });
  }

  @Action(SetViewedSalesArticles)
  private setViewedSalesArticles(ctx: StateContext<SalesArticleStateModel>, action: SetViewedSalesArticles) {
    return ctx.patchState({ viewed: action.salesArticles });
  }

  @Action(RefreshViewedSalesArticles)
  private refreshViewedSalesArticles(ctx: StateContext<SalesArticleStateModel>) {
    const viewed = this.webStorageService.get<SalesArticleSummary[]>(viewedSalesArticleKey);

    if (!viewed?.length) {
      return [];
    }

    return this.salesArticleService
      .apiProductsquerySalesarticlesSummaryIdsGet({ ids: viewed.map((x) => x.id) })
      .subscribe((response: SalesArticleSummary[]) => {
        const sorted: SalesArticleSummary[] = [];

        viewed.forEach((v) => {
          const filtered = response.filter((x) => x.id === v.id);
          if (filtered.length === 1) {
            sorted.push(filtered[0]);
          }
        });

        return ctx.patchState({ viewed: sorted });
      });
  }
}
